import { useChatRooms } from '@commenda/api';
import {
  Button,
  IconButton,
  Icons,
  Tooltip,
  useClickOutside,
  useToggle,
} from '@commenda/kakashi';
import { ChatRoomModel } from '@commenda/utils';
import { forwardRef, RefObject, useCallback, useMemo } from 'react';
import { useDisableOverflow } from '../../hooks/useDisableOverflow';
import { AllChatRooms } from '../ChatRoomList';

interface ChatRoomsTriggerProps {
  businessEntityId?: number;
  companyId: number;
  hideLabel?: boolean;
  chatRoomId?: string;
  disableChatBackButton?: boolean;
  userId: string;
  includeInternalNotes: boolean;
  hideText?: boolean;
}

// eslint-disable-next-line react/display-name
export const ChatRoomsTrigger = forwardRef<
  HTMLDivElement,
  ChatRoomsTriggerProps
>(
  (
    {
      businessEntityId,
      companyId,
      hideLabel = false,
      chatRoomId,
      disableChatBackButton = false,
      userId,
      includeInternalNotes,
      hideText = false,
    },
    mainContainerRef?: RefObject<HTMLDivElement>
  ) => {
    const chatRoomVisibility = useToggle(false);
    const { data: chatRooms } = useChatRooms({
      businessEntityId,
      companyId,
    });

    if (mainContainerRef) {
      useDisableOverflow(
        mainContainerRef,
        chatRoomVisibility.value,
        'hidden',
        'scroll'
      );
    }

    // Function to close the chat rooms dropdown
    const handleClickOutside = useCallback(() => {
      if (chatRoomVisibility.value) {
        chatRoomVisibility.setValue(false);
      }
    }, [chatRoomVisibility]);

    // Use the click outside hook
    const containerRef = useClickOutside<HTMLDivElement>(handleClickOutside);

    const hasUnreadMessages = useMemo(() => {
      if (!chatRooms?.chatRooms) return false;

      if (chatRoomId) {
        const specificChatRoom = chatRooms.chatRooms.find(
          (room) => room.id === chatRoomId
        );
        if (!specificChatRoom) return false;
        return ChatRoomModel.hasUnread(specificChatRoom, includeInternalNotes);
      }
      return chatRooms.chatRooms.some((room) =>
        ChatRoomModel.hasUnread(room, includeInternalNotes)
      );
    }, [chatRooms, chatRoomId, includeInternalNotes]);

    return (
      <div className="relative" ref={containerRef}>
        {hideLabel ? (
          <Button
            id={ChatRoomModel.getButtonId(chatRoomId)}
            onClick={chatRoomVisibility.toggle}
            variant="tertiary"
            size="sm"
            className="h-fit"
          >
            <Icons.Inbox01 className="size-5" />
            {hasUnreadMessages && (
              <div className="absolute top-[-2px] right-[-2px] w-3 h-3 bg-blue-500 rounded-full border-2 border-white" />
            )}
          </Button>
        ) : hideText ? (
          <Tooltip content="Messages" asChild>
            <IconButton
              icon={<Icons.MessageCircle01 />}
              aria-label="Messages"
              onClick={chatRoomVisibility.toggle}
              size="lg"
            />
          </Tooltip>
        ) : (
          <Button
            id={ChatRoomModel.getButtonId(chatRoomId)}
            onClick={chatRoomVisibility.toggle}
            className="flex items-center gap-md h-fit"
            size="sm"
            variant="tertiary"
          >
            <div className="relative top-0 left-0">
              <Icons.Inbox01 className="size-5" />
              {hasUnreadMessages && (
                <div className="absolute top-[-2px] right-[-2px] w-3 h-3 bg-blue-500 rounded-full border-2 border-white" />
              )}
            </div>
            Messages
          </Button>
        )}
        <div className="absolute right-0">
          <AllChatRooms
            open={chatRoomVisibility.value}
            businessEntityId={businessEntityId}
            companyId={companyId}
            chatRoomId={chatRoomId}
            disableChatBackButton={disableChatBackButton}
            userId={userId}
          />
        </div>
      </div>
    );
  }
);
